import type { BlockId } from '@unifyapps/defs/types/block';
import { useCallback } from 'react';
import { getPulseOwner } from '../../pulse/executionOwner';
import { pulse } from '../../pulse/floor';
import type { EntityRefLite, ErrorSource } from '../../pulse/types';
import { usePulseScope } from '../usePulseScope';

interface ReportActionErrorParams {
  // what threw: 'runScript' for script/eval actions, 'pageFunction' for page functions
  source: ErrorSource;
  error: unknown;
  instanceId?: string;
  // the block whose action threw — becomes the error row's entity
  blockId?: BlockId;
  // explicit entity override, wins over blockId: a page function attributes its own throw to itself
  // (not the block that triggered it), so its error and its logs point to the same place
  entity?: EntityRefLite;
  // the user code that threw (script / function body) — shown on the error's Expression tab
  expression?: string;
  // structured input for non-code errors (e.g. a rejected set-variable) — rendered as an object
  // viewer in the error's Payload view. Prefer this over stringifying into `expression`.
  payload?: unknown;
}

/**
 * Reports an action's thrown error to Pulse as an `error` event. Centralizes what the
 * executeScript / evalJS / triggerFunction action hooks each used to inline: scope resolution,
 * the source-block entity, and the "capture must never break the action" guard. Unattributable
 * (`unknown:`) scopes are skipped so a provider-less mount never posts a phantom-scope error.
 */
export function useReportActionError() {
  const resolveScope = usePulseScope();

  return useCallback(
    ({ source, error, instanceId, blockId, entity, expression, payload }: ReportActionErrorParams) => {
      try {
        const scope = resolveScope(instanceId);
        if (scope.startsWith('unknown:')) return;
        pulse.error(scope, source, String((error as Error)?.message ?? error), {
          expression,
          stack: (error as Error)?.stack,
          // explicit entity wins; else the triggering block; else the function currently executing
          entity: entity ?? (blockId ? { type: 'block', id: blockId } : getPulseOwner()),
          payload,
        });
      } catch {
        /* capture must never break the action */
      }
    },
    [resolveScope],
  );
}
